import { useQuery } from '@tanstack/react-query';
import { getWhatsAppTemplates } from '../../api';
import { TemplatePhonePreview } from '../../components/TemplatePhonePreview';
import { SideDrawer, Button, TableChip } from '@/components';
import { useReminderDetail } from '../hooks';
import { getReminderStatusMeta } from '../utils';
import { formatInterval } from '../../utils/intervalUtils';
import {
  CONTACT_NOT_IN_CRM_COPY,
  getReminderActionButtons,
  shouldShowContactMissing,
} from '../utils/reminderActions';

const ACTION_BUTTONS = {
  stop: { label: 'Stop', variant: 'secondary' },
  reschedule: { label: 'Reschedule', variant: 'primary' },
  pause: { label: 'Pause', variant: 'secondary' },
  resume: { label: 'Resume', variant: 'primary' },
};

export function ReminderDetailModal({
  companyId,
  reminderId,
  onClose,
  onAction,
  canUpdate = true,
  isBusy = false,
}) {
  const {
    data: reminder,
    isLoading,
    isError,
  } = useReminderDetail(companyId, reminderId);

  const isTemplate =
    reminder?.message_type === 'template' ||
    !!reminder?.template_id ||
    !!reminder?.meta?.template_id ||
    !!reminder?.template_name ||
    !!reminder?.meta?.template_name ||
    reminder?.type === 'whatsapp_reminder';

  const { data: templatesResponse, isLoading: isTemplatesLoading } = useQuery({
    queryKey: ['whatsapp-templates', companyId],
    queryFn: () => getWhatsAppTemplates(companyId),
    enabled: !!companyId && !!reminder && isTemplate,
  });

  const templates = templatesResponse?.results || templatesResponse || [];

  const templateNameFallback =
    reminder?.template_name ||
    reminder?.meta?.template_name ||
    reminder?.description;
  const templateIdFallback =
    reminder?.template_id || reminder?.meta?.template_id;
  const selectedTemplate = templates.find(
    (t) =>
      (templateIdFallback &&
        String(t.id || t.uuid) === String(templateIdFallback)) ||
      (templateNameFallback && t.name === templateNameFallback),
  );

  let templateValues = {};
  const rawVars =
    reminder?.template_variables || reminder?.meta?.template_variables;
  if (rawVars) {
    try {
      templateValues =
        typeof rawVars === 'string' ? JSON.parse(rawVars) : rawVars;
    } catch {
      // Ignore parse errors
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const status = reminder?.status;
  const statusMeta = status ? getReminderStatusMeta(status) : null;
  const scheduleType =
    reminder?.meta?.schedule_type || reminder?.meta?.follow_up_type;
  const isInterval = scheduleType === 'interval';
  const isYearly = scheduleType === 'yearly_date';
  const contactMissing = shouldShowContactMissing(reminder);
  const actions =
    canUpdate && !contactMissing ? getReminderActionButtons(reminder) : [];

  return (
    <SideDrawer
      isOpen={!!reminderId}
      onClose={onClose}
      width="md"
      header={
        <div className="flex items-center justify-between w-full">
          <h2 className="text-xl font-bold text-[#1F2241]">Reminder Details</h2>
          <div className="flex items-center gap-3">
            {!isLoading && !isError && statusMeta && (
              <TableChip tone={statusMeta.tone}>{statusMeta.label}</TableChip>
            )}
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-[8px] bg-primary text-white flex items-center justify-center hover:bg-primary-hover transition-colors focus:outline-none shrink-0"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.75"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      }
      footer={
        !isLoading && !isError && reminder && actions.length > 0 ? (
          <div className="flex justify-end w-full gap-3">
            {actions.map((action) => {
              const meta = ACTION_BUTTONS[action];
              if (!meta) return null;
              const isStop = action === 'stop';
              return (
                <Button
                  key={action}
                  variant={meta.variant}
                  onClick={() => onAction?.({ type: action, reminder })}
                  disabled={isBusy}
                  className={`h-10 px-4 text-sm ${isStop ? 'text-red-500' : ''}`}
                >
                  {meta.label}
                </Button>
              );
            })}
          </div>
        ) : null
      }
    >
      <div className="p-6">
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        ) : isError || !reminder ? (
          <p className="text-sm text-red-500 text-center py-4">
            Failed to load reminder details.
          </p>
        ) : (
          <div className="flex flex-col gap-6">
            {contactMissing && (
              <p className="text-sm text-[#C98A1F] bg-[#FBEFD1] rounded-[12px] px-4 py-3">
                {CONTACT_NOT_IN_CRM_COPY}
              </p>
            )}

            <div>
              <h4 className="text-xs font-semibold text-[#868686] tracking-wider mb-2">
                Message Content
              </h4>
              <div className="p-4 rounded-[12px] bg-[#F5F5F5]">
                {isTemplate ? (
                  <div className="flex flex-col gap-6 items-center w-full">
                    <div className="flex flex-col gap-1 w-full text-center">
                      <span className="text-sm font-semibold text-[#1F2241]">
                        Template: {templateNameFallback || '-'}
                      </span>
                    </div>

                    <div className="flex-shrink-0 mx-auto">
                      {isTemplatesLoading ? (
                        <div className="w-[260px] h-[300px] flex items-center justify-center bg-white border border-[#EBF4EA] rounded-[34px]">
                          <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                        </div>
                      ) : selectedTemplate ? (
                        <TemplatePhonePreview
                          template={selectedTemplate}
                          templateValues={templateValues}
                        />
                      ) : (
                        <div className="w-[260px] p-4 text-center text-sm text-[#868686] bg-white border border-[#EBF4EA] rounded-xl">
                          Template preview unavailable
                        </div>
                      )}
                    </div>

                    {Object.keys(templateValues).length > 0 && (
                      <div className="flex flex-col gap-2 bg-white rounded-lg p-4 border border-[#EBF4EA] w-full text-left">
                        <span className="text-xs font-semibold text-[#868686] uppercase tracking-wider mb-1">
                          Variables
                        </span>
                        <div className="grid grid-cols-2 gap-3">
                          {Object.entries(templateValues).map(([key, val]) => (
                            <div key={key} className="flex flex-col">
                              <span className="text-[10px] text-[#868686] truncate mb-0.5">
                                {key}
                              </span>
                              <span
                                className="text-sm font-medium text-[#1F2241] break-words"
                                title={String(val)}
                              >
                                {String(val)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1 w-full">
                      <span className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                        Title
                      </span>
                      <span className="text-sm font-semibold text-[#1F2241]">
                        {reminder.title || '-'}
                      </span>
                    </div>
                    {reminder.description && (
                      <div className="flex flex-col gap-1 w-full mt-2">
                        <span className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                          Description
                        </span>
                        <p className="text-sm text-[#1F2241] whitespace-pre-wrap break-words font-medium">
                          {reminder.description}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5]">
                <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                  Reminder Type
                </div>
                <p className="text-sm font-medium text-[#1F2241] capitalize">
                  {reminder.type?.replace(/_/g, ' ') || '-'}
                </p>
              </div>

              <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5]">
                <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                  Created At
                </div>
                <p className="text-sm font-medium text-[#1F2241]">
                  {formatDate(reminder.created_at)}
                </p>
              </div>

              {isYearly ? (
                <>
                  <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5] sm:col-span-2">
                    <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                      Schedule
                    </div>
                    <p className="text-sm font-medium text-[#1F2241]">
                      Yearly date
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5]">
                    <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                      Next send
                    </div>
                    <p className="text-sm font-medium text-[#1F2241]">
                      {formatDate(reminder.remind_at)}
                    </p>
                  </div>
                  {(reminder.meta?.event_month != null ||
                    reminder.meta?.event_day != null) && (
                    <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5]">
                      <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                        Event date
                      </div>
                      <p className="text-sm font-medium text-[#1F2241]">
                        {reminder.meta?.event_day ?? '-'} /{' '}
                        {reminder.meta?.event_month ?? '-'}
                      </p>
                    </div>
                  )}
                </>
              ) : !isInterval ? (
                <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5] sm:col-span-2">
                  <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                    Scheduled For
                  </div>
                  <p className="text-sm font-medium text-[#1F2241]">
                    {formatDate(reminder.remind_at)}
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5]">
                    <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                      Interval
                    </div>
                    <p className="text-sm font-medium text-[#1F2241]">
                      {formatInterval(
                        reminder.meta?.interval_value,
                        reminder.meta?.interval_unit,
                      )}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5]">
                    <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                      Stop On Reply
                    </div>
                    <p className="text-sm font-medium text-[#1F2241]">
                      {reminder.meta?.stop_on_reply ? 'Yes' : 'No'}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5]">
                    <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                      Start Date
                    </div>
                    <p className="text-sm font-medium text-[#1F2241]">
                      {formatDate(
                        reminder.meta?.start_date || reminder.created_at,
                      )}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5]">
                    <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                      End Date
                    </div>
                    <p className="text-sm font-medium text-[#1F2241]">
                      {reminder.meta?.end_date
                        ? formatDate(reminder.meta.end_date)
                        : 'No end date'}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5]">
                    <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                      Attempts
                    </div>
                    <p className="text-sm font-medium text-[#1F2241]">
                      {reminder.meta?.completed_attempts || 0} /{' '}
                      {reminder.meta?.max_attempts || 'Unlimited'}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5]">
                    <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                      Next Execution
                    </div>
                    <p className="text-sm font-medium text-[#1F2241]">
                      {formatDate(reminder.remind_at)}
                    </p>
                  </div>
                </>
              )}
            </div>

            {reminder.contact && (
              <div>
                <h4 className="text-xs font-semibold text-[#868686] tracking-wider mb-2">
                  Contact Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5]">
                    <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                      Name
                    </div>
                    <p className="text-sm font-medium text-[#1F2241]">
                      {reminder.contact.name || '-'}
                    </p>
                  </div>

                  <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5]">
                    <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                      {reminder.contact.email
                        ? 'Email Address'
                        : 'Phone Number'}
                    </div>
                    <p className="text-sm font-medium text-[#1F2241]">
                      {reminder.contact.email || reminder.contact.phone || '-'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </SideDrawer>
  );
}
